/**
 * What we owe the agency, and what we have already sent.
 *
 * Only money collected through **our** provider account appears here. An agency on its own keys
 * never had its rent pass through us and will see nothing but an explanation of why.
 *
 * **A run is a statement.** Once drawn it is fixed, it names the payments it covers, and it can be
 * opened and reconciled against a bank statement line. Recomputing it later from whatever the data
 * has since become is how a disagreement about money starts.
 */

import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { formValues, useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Spinner,
  Table,
} from "@/components/ui";

interface Settlement {
  id: string;
  reference: string;
  periodStart: string;
  periodEnd: string;
  currency: string;
  gross: number;
  providerFees: number;
  platformFees: number;
  refunded: number;
  net: number;
  carriedForward: number;
  chargeCount: number;
  status: "draft" | "sent" | "paid" | "failed";
  transferReference: string | null;
  paidAt: string | null;
}

interface Charge {
  id: string;
  reference: string;
  provider: string;
  status: string;
  amount: number;
  providerFee: number;
  platformFee: number;
  net: number;
  refunded: number;
  payerName: string | null;
  paidAt: string | null;
  settlementId: string | null;
}

const STATUS_TONE: Record<Settlement["status"], "warn" | "info" | "success" | "danger"> = {
  draft: "warn",
  sent: "info",
  paid: "success",
  failed: "danger",
};

export function SettlementsPage() {
  const fmt = useFormat();
  const { can } = useSession();
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["settlements"],
    queryFn: () => api.get<{ settlements: Settlement[]; unsettled: { count: number; net: number } }>("/settlements"),
  });

  const charges = useQuery({
    queryKey: ["charges"],
    queryFn: () => api.get<{ charges: Charge[]; simulated: boolean }>("/charges"),
  });

  const detail = useQuery({
    queryKey: ["settlement", openId],
    queryFn: () => api.get<{ settlement: Settlement; charges: Charge[] }>(`/settlements/${openId}`),
    enabled: Boolean(openId),
  });

  const run = useApiMutation({
    mutationFn: (input: Record<string, string>) => api.post("/settlements/run", input),
    invalidates: [["settlements"], ["charges"]],
  });

  const markPaid = useApiMutation({
    mutationFn: (input: { id: string; transferReference: string }) =>
      api.post(`/settlements/${input.id}/paid`, { transferReference: input.transferReference || null }),
    invalidates: [["settlements"], ["settlement"]],
  });

  const draw = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    run.mutate(formValues(event.currentTarget));
  };

  if (list.error) return <ErrorState error={list.error} retry={() => list.refetch()} />;

  const unsettled = list.data?.unsettled ?? { count: 0, net: 0 };
  const anyCharges = (charges.data?.charges.length ?? 0) > 0;

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Settlements"
        description="Money your clients paid through us, what it cost, and when it was sent on to you."
      />

      {charges.data?.simulated && anyCharges ? (
        <div className="mb-5">
          {/* The same honesty the payment page owes a payer, owed here to the agency. */}
          <Alert tone="warn">
            No card provider is connected on this deployment, so nothing below was really collected. The figures are
            real arithmetic on charges that were recorded but never taken.
          </Alert>
        </div>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardTitle
            hint={
              unsettled.count === 0
                ? "Nothing is waiting to be paid out."
                : `${unsettled.count} payment${unsettled.count === 1 ? "" : "s"} collected and not yet settled.`
            }
          >
            Waiting to be paid out
          </CardTitle>

          <p className="tabular text-3xl font-semibold text-ink-900">{fmt.money(unsettled.net)}</p>

          {can("org.settings") ? (
            <form onSubmit={draw} className="mt-5 grid gap-3 sm:grid-cols-3">
              {run.errorMessage ? (
                <div className="sm:col-span-3">
                  <Alert tone="danger">{run.errorMessage}</Alert>
                </div>
              ) : null}

              <Field label="From" htmlFor="periodStart" error={run.fieldErrors.periodStart}>
                <Input id="periodStart" name="periodStart" type="date" required />
              </Field>
              <Field label="To" htmlFor="periodEnd" error={run.fieldErrors.periodEnd}>
                <Input id="periodEnd" name="periodEnd" type="date" required />
              </Field>
              <div className="flex items-end">
                <Button type="submit" disabled={run.isPending || unsettled.count === 0}>
                  {run.isPending ? "Drawing…" : "Draw a run"}
                </Button>
              </div>
            </form>
          ) : null}
        </Card>

        <Card>
          <CardTitle hint="Every attempt, whether or not it succeeded.">Charges</CardTitle>
          {charges.isLoading ? (
            <Spinner />
          ) : !anyCharges ? (
            <EmptyState
              title="Nothing collected through us yet."
              hint="Switch a card option on under Bank accounts and your clients can pay online."
            />
          ) : (
            <ul className="divide-y divide-ink-100 text-sm">
              {charges.data!.charges.slice(0, 8).map((charge) => (
                <li key={charge.id} className="flex items-baseline justify-between gap-3 py-2">
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-ink-900">{charge.payerName ?? "Online payment"}</span>
                    <span className="block truncate text-xs text-ink-500">
                      {charge.provider} · {charge.status}
                      {charge.refunded > 0 ? ` · ${fmt.money(charge.refunded)} refunded` : ""}
                    </span>
                  </span>
                  <span className="tabular shrink-0 font-medium">{fmt.money(charge.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="mt-5" padded={false}>
        <div className="p-4 sm:p-5">
          <CardTitle hint="Each one names the payments it covers.">Runs</CardTitle>
        </div>

        {list.isLoading ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : !list.data?.settlements.length ? (
          <div className="p-5">
            <EmptyState title="No runs yet." hint="Draw one above once something has been collected." />
          </div>
        ) : (
          <Table
            head={
              <tr>
                <th>Reference</th>
                <th>Period</th>
                <th className="num">Payments</th>
                <th className="num">Fees</th>
                <th className="num">Net</th>
                <th>Status</th>
                <th />
              </tr>
            }
          >
            {list.data.settlements.map((settlement) => (
              <tr key={settlement.id}>
                <td className="mono text-xs font-medium">{settlement.reference}</td>
                <td className="text-ink-600">
                  {fmt.date(settlement.periodStart)} – {fmt.date(settlement.periodEnd)}
                </td>
                <td className="num tabular">{settlement.chargeCount}</td>
                <td className="num tabular text-ink-500">
                  {fmt.money(settlement.providerFees + settlement.platformFees)}
                </td>
                <td className="num tabular font-medium">{fmt.money(settlement.net)}</td>
                <td>
                  <Badge tone={STATUS_TONE[settlement.status]} dot>
                    {settlement.status}
                  </Badge>
                </td>
                <td className="num">
                  <button
                    type="button"
                    className="text-sm text-ink-500 hover:text-ink-800"
                    onClick={() => setOpenId(openId === settlement.id ? null : settlement.id)}
                  >
                    {openId === settlement.id ? "Hide" : "Open"}
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {openId && detail.data ? (
        <Card className="mt-5">
          <CardTitle
            hint={`${detail.data.settlement.chargeCount} payment${detail.data.settlement.chargeCount === 1 ? "" : "s"}, ${fmt.money(detail.data.settlement.net)} net.`}
            action={
              detail.data.settlement.status !== "paid" && can("org.settings") ? (
                <form
                  className="flex items-center gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const value = String(new FormData(event.currentTarget).get("transferReference") ?? "");
                    markPaid.mutate({ id: openId, transferReference: value });
                  }}
                >
                  <Input name="transferReference" placeholder="Bank reference" className="w-40" />
                  <Button size="sm" type="submit" disabled={markPaid.isPending}>
                    Mark sent
                  </Button>
                </form>
              ) : detail.data.settlement.transferReference ? (
                <span className="mono text-xs text-ink-500">{detail.data.settlement.transferReference}</span>
              ) : undefined
            }
          >
            {detail.data.settlement.reference}
          </CardTitle>

          {markPaid.errorMessage ? <Alert tone="danger">{markPaid.errorMessage}</Alert> : null}

          <dl className="mb-4 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
            {[
              ["Collected", detail.data.settlement.gross],
              ["Provider kept", detail.data.settlement.providerFees],
              ["We kept", detail.data.settlement.platformFees],
              ["Sent to you", detail.data.settlement.net],
            ].map(([label, value]) => (
              <div key={String(label)}>
                <dt className="eyebrow">{label}</dt>
                <dd className="tabular font-medium">{fmt.money(Number(value))}</dd>
              </div>
            ))}
          </dl>

          <Table
            head={
              <tr>
                <th>Payer</th>
                <th>Reference</th>
                <th>Paid</th>
                <th className="num">Amount</th>
                <th className="num">Fees</th>
                <th className="num">Net</th>
              </tr>
            }
          >
            {detail.data.charges.map((charge) => (
              <tr key={charge.id}>
                <td>{charge.payerName ?? "—"}</td>
                <td className="mono text-xs">{charge.reference.slice(0, 14)}</td>
                <td className="text-ink-600">{charge.paidAt ? fmt.dateTime(charge.paidAt) : "—"}</td>
                <td className="num tabular">{fmt.money(charge.amount)}</td>
                <td className="num tabular text-ink-500">{fmt.money(charge.providerFee + charge.platformFee)}</td>
                <td className="num tabular font-medium">{fmt.money(charge.net - charge.refunded)}</td>
              </tr>
            ))}
          </Table>
        </Card>
      ) : null}
    </div>
  );
}
